import * as React from 'react'
import {ReactElement} from 'react'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {StyledSecondaryButton} from '../../../../styledComponents/Button'
import {Box} from '@mui/material'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {Nullable} from '../../../../app/TypeHelpers'
import {RESIDENT_APPLY_STATUS} from '../../../../CodeMaster'
import {NatResidentApplicationHistoryInfoComponent} from '../common/resident/NatResidentApplicationHistoryInfoComponent'
import styled from 'styled-components'
import {NatPetBreedingStopDetail} from '../repository/NatGetPetBreedingStop'
import {FullWidthColumnFlexContainer} from '../../../../styledComponents/FlexComponents'
import {NatGetApplicationResultDetail} from '../repository/NatGetApplicationResult'
import {HKConstants} from '../../../../HKConstants'
import AuthorityChecker from '../../../../components/AuthorityChecker'
import NatResidentPetBreedingStopConfirm from './resident/NatResidentPetBreedingStopConfirm'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {convertApplyStatusIntoApplyHistoryDivision} from '../common/helper/NatApplicationHelper'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId: string
    applicationRepo: NatApplicationRepo
    location: Location
}

interface States {
    petBreedingStopDetail: NatPetBreedingStopDetail
    applicationResultDetail: NatGetApplicationResultDetail
    isLoadCompleted: boolean
}

class NatResidentPetBreedingStopPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, States> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            petBreedingStopDetail: {
                applyId: '',
                applyStatus: '',
                condominiumCd: '',
                condominiumName: '',
                ridgeName: '',
                roomNumber: '',
                applicantFamilyName: '',
                applicantFirstName: '',
                applyDate: '',
                petType: '',
                petBreed: '',
                havePetEndDate: '',
                havePetEndReason: '',
                otherReason: '',
                version: 0,
            },
            applicationResultDetail: {
                applyInfo: {
                    applyId: '',
                    applyCategory: '',
                    ridgeName: '',
                    roomNumber: '',
                    familyName: '',
                    firstName: '',
                    registerDatetime: '',
                },
                resultInfo: {
                    applyStatus: '',
                    roleId: '',
                    comment: '',
                    registerDatetime: '',
                },
            },
            isLoadCompleted: false,
        }

        this.onClickReApplyButton = this.onClickReApplyButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getApplyInfo()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({
                    isLoadCompleted: true,
                },
            )
        }
    }

    async getApplyInfo(): Promise<void> {
        const [petBreedingStopResponse, applicationStatusResponse] = await Promise.all([
            this.getPetBreedingStop(),
            this.getApplicationResult(),
        ])

        if (petBreedingStopResponse && applicationStatusResponse) {
            this.setState({
                petBreedingStopDetail: petBreedingStopResponse,
                applicationResultDetail: applicationStatusResponse,
            })
        }
    }

    async getPetBreedingStop(): Promise<Nullable<NatPetBreedingStopDetail>> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getPetBreedingStopForResident.bind(applicationRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    async getApplicationResult(): Promise<Nullable<NatGetApplicationResultDetail>> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getApplyResult.bind(applicationRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    onClickReApplyButton(): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/applications/petBreedingStop/${this.props.applyId}/copy`,
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>
                <PlainBackLink to={`/resident/condominiums/${this.props.condominiumCd}/applications/history`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請内容
                </PageTitle>
                <Box height={'20px'}/>
                <NatResidentApplicationHistoryInfoComponent
                    applyId={this.props.applyId}
                    applyHistoryDivision={
                        convertApplyStatusIntoApplyHistoryDivision(this.state.petBreedingStopDetail.applyStatus)}
                    applyResultInfo={this.state.applicationResultDetail}
                />
                <NatResidentPetBreedingStopConfirm
                    {...this.state.petBreedingStopDetail}
                    applyFlag={false}
                />
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.APPLY_PET_BREEDING_STOP}>
                    {(this.state.petBreedingStopDetail.applyStatus === RESIDENT_APPLY_STATUS.COMPLETE.value ||
                            this.state.petBreedingStopDetail.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) &&
                        <ReApplyButton
                            onClick={this.onClickReApplyButton}>
                            <FullWidthColumnFlexContainer>
                                <div>
                                    内容を編集して再申請する
                                </div>
                            </FullWidthColumnFlexContainer>
                        </ReApplyButton>
                    }
                </AuthorityChecker>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentPetBreedingStopPage)

export const ReApplyButton = styled(StyledSecondaryButton)`
  display: flex;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 15px;
`
